/**
 * Report Results Table Component
 * Extracted from ReportingDashboard.jsx
 * Displays report results with pagination + contextual summary chips.
 */
import React from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Calendar, CalendarDays } from 'lucide-react';
import { formatFullAddress, EMPTY_PLACEHOLDER } from '../../../utils/textFormat';
import { normalizeBorrowerName } from '../../../utils/nameNormalizer';
import { formatDateOnly } from '../../../utils/formatters';

/**
 * Soft status pill colors — SaaS-style, not harsh.
 */
const statusChipClasses = (status) => {
  if (!status) return 'bg-slate-100 text-slate-600 border-slate-200';
  const s = status.toLowerCase();
  if (s === 'completed' || s === 'complete') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (s === 'open') return 'bg-sky-50 text-sky-700 border-sky-200';
  if (s === 'pending') return 'bg-amber-50 text-amber-700 border-amber-200';
  if (s === 'clarification') return 'bg-rose-50 text-rose-700 border-rose-200';
  if (s === 'qc_review' || s === 'qc review') return 'bg-violet-50 text-violet-700 border-violet-200';
  if (s === 'cancelled' || s === 'canceled') return 'bg-slate-100 text-slate-500 border-slate-200';
  if (s.includes('mail')) return 'bg-indigo-50 text-indigo-700 border-indigo-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
};

const priorityChipClasses = (priority) => {
  const p = (priority || 'normal').toLowerCase();
  if (p === 'urgent') return 'bg-rose-50 text-rose-700 border-rose-200';
  if (p === 'rush') return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-slate-50 text-slate-600 border-slate-200';
};

/**
 * Format property address from order data
 */
const formatPropertyAddress = (order) => {
  if (!order) return EMPTY_PLACEHOLDER;
  return formatFullAddress({
    street: order.property_address,
    city:   order.property_city,
    state:  order.property_state,
    zip:    order.property_zip,
  });
};

/**
 * Short, readable date range chip e.g. "Apr 12 – Apr 19"
 * Falls back to the raw values on parse failure.
 *
 * Jul 2026 fix — plain `YYYY-MM-DD` values (the user's raw date-picker
 * selection) are formatted timezone-free. Previously `new Date(iso)`
 * parsed them as UTC midnight and `toLocaleDateString` re-rendered them
 * in the browser's local zone, shifting a 06/01–06/30 selection to
 * "May 31 – Jun 29" for US users.
 */
const formatDateLabel = (value, opts) => {
  if (!value) return null;
  return formatDateOnly(value);
};

const formatDateRangeLabel = (startIso, endIso) => {
  if (!startIso && !endIso) return null;
  try {
    const opts = { month: 'short', day: 'numeric' };
    const s = formatDateLabel(startIso, opts);
    const e = formatDateLabel(endIso, opts);
    if (s && e) return `${s} – ${e}`;
    return s || e;
  } catch {
    return `${startIso || ''}${startIso && endIso ? ' – ' : ''}${endIso || ''}`;
  }
};

/**
 * Split an ET-formatted string into { datePart, timePart } for the two-line
 * display requested in the design brief.
 * formatUtcToET returns e.g. "04/17/2026 07:55 AM ET" or "Apr 17, 2026 7:55 AM ET".
 * We split on the first space that separates the date token(s) from the time token.
 */
const splitDateTime = (formatted) => {
  if (!formatted || formatted === 'N/A') return { datePart: 'N/A', timePart: '' };
  // Match leading date followed by whitespace + time pattern "HH:MM[:SS] AM/PM ET"
  const m = formatted.match(/^(.+?)\s+(\d{1,2}:\d{2}(?::\d{2})?\s+[AP]M(?:\s+ET)?)\s*$/i);
  if (m) {
    return { datePart: m[1].trim(), timePart: m[2].trim() };
  }
  return { datePart: formatted, timePart: '' };
};

/**
 * Report Results Table
 */
export const ReportResultsTable = ({
  reportResults,
  currentPage,
  setCurrentPage,
  queryTime, // eslint-disable-line no-unused-vars -- kept for prop-compat, intentionally not shown
  formatUtcToET,
  getTimestampTooltip,
  filters,
}) => {
  if (!reportResults?.orders || reportResults.orders.length === 0) {
    return null;
  }

  const pageSize = 50;
  const totalOrders = reportResults.total_count || reportResults.orders.length;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(currentPage * pageSize, totalOrders);
  const displayedOrders = reportResults.orders.slice(startIndex, endIndex);

  // Contextual chips for the summary row.
  // Jul 2026 fix — prefer the user's RAW selected dates (filters) over the
  // backend's UTC window (odu_report.date_range); the latter starts at the
  // selected day's UTC midnight, which renders one day early in US zones.
  const dateRangeLabel =
    (filters &&
      formatDateRangeLabel(filters.customStartDate, filters.customEndDate)) ||
    (reportResults.odu_report?.date_range &&
      formatDateRangeLabel(
        reportResults.odu_report.date_range.start,
        reportResults.odu_report.date_range.end,
      ));

  const dateTypeLabel = filters?.dateType === 'closure' ? 'Closure Date' : filters?.dateType === 'creation' ? 'Creation Date' : null;

  return (
    <Card className="shadow-sm border border-slate-200 rounded-xl" data-testid="report-results-card">
      {/* Summary chips row — replaces the old "Report Results" heading */}
      <div className="px-6 pt-5 pb-4 flex flex-wrap items-center gap-2" data-testid="report-results-summary">
        <span
          className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100 px-3 py-1 text-xs font-semibold"
          data-testid="report-results-count-badge"
        >
          {totalOrders} {totalOrders === 1 ? 'Order' : 'Orders'} Found
        </span>

        {dateRangeLabel && (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 text-slate-700 border border-slate-200 px-3 py-1 text-xs font-medium"
            data-testid="report-results-daterange-chip"
          >
            <Calendar className="h-3 w-3 text-slate-500" />
            {dateRangeLabel}
          </span>
        )}

        {dateTypeLabel && (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 text-slate-700 border border-slate-200 px-3 py-1 text-xs font-medium"
            data-testid="report-results-datetype-chip"
          >
            <CalendarDays className="h-3 w-3 text-slate-500" />
            {dateTypeLabel}
          </span>
        )}

        {/* Carry-Forward chip (Feb 2026 — Option C). The old static
            "Show carried forward" label has been replaced by a chip
            that reflects the actual toggle state set on the filter
            card, and shows the prior-period count when the toggle is
            ON so operators understand the row composition. */}
        {filters?.includeCarryForward === false ? (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 px-3 py-1 text-xs font-medium"
            data-testid="report-results-carryforward-chip"
            title="Strict date-range filtering — active prior-period orders excluded."
          >
            Strict date range
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 px-3 py-1 text-xs font-medium"
            data-testid="report-results-carryforward-chip"
            title="Active prior-period orders are included alongside the in-range result set."
          >
            Carry-forward: ON
            {reportResults.carry_forward_count > 0 && (
              <span className="font-semibold">
                · {reportResults.carry_forward_count}
              </span>
            )}
          </span>
        )}
      </div>

      <CardContent className="p-0">
        <div className="w-full overflow-hidden">
          <table className="w-full text-sm border-collapse table-fixed">
            <thead className="tf-thead">
              <tr>
                <th className="h-11 px-3 text-left align-middle font-semibold text-slate-700 text-xs uppercase tracking-wide" style={{ width: '8%' }}>Order ID</th>
                <th className="h-11 px-3 text-left align-middle font-semibold text-slate-700 text-xs uppercase tracking-wide" style={{ width: '7%' }}>Product</th>
                <th className="h-11 px-3 text-left align-middle font-semibold text-slate-700 text-xs uppercase tracking-wide" style={{ width: '10%' }}>Borrower</th>
                <th className="h-11 px-3 text-left align-middle font-semibold text-slate-700 text-xs uppercase tracking-wide" style={{ width: '15%' }}>Property Address</th>
                <th className="h-11 px-3 text-left align-middle font-semibold text-slate-700 text-xs uppercase tracking-wide" style={{ width: '11%' }}>Status</th>
                <th className="h-11 px-3 text-left align-middle font-semibold text-slate-700 text-xs uppercase tracking-wide" style={{ width: '7%' }}>Priority</th>
                <th className="h-11 px-3 text-left align-middle font-semibold text-slate-700 text-xs uppercase tracking-wide" style={{ width: '8%' }}>Client Code</th>
                <th className="h-11 px-3 text-left align-middle font-semibold text-slate-700 text-xs uppercase tracking-wide" style={{ width: '12%' }}>Assigned</th>
                <th className="h-11 px-3 text-left align-middle font-semibold text-slate-700 text-xs uppercase tracking-wide" style={{ width: '11%' }}>Created</th>
                <th className="h-11 px-3 text-left align-middle font-semibold text-slate-700 text-xs uppercase tracking-wide" style={{ width: '11%' }}>Modified</th>
              </tr>
            </thead>
            <tbody>
              {displayedOrders.map((order, index) => {
                const created = order.created_at && formatUtcToET ? splitDateTime(formatUtcToET(order.created_at)) : { datePart: 'N/A', timePart: '' };
                const updated = order.updated_at && formatUtcToET ? splitDateTime(formatUtcToET(order.updated_at)) : { datePart: 'N/A', timePart: '' };

                return (
                  <tr
                    key={order.order_number || order.id || index}
                    className="transition-colors hover:bg-blue-50/40 border-b border-slate-100"
                    data-testid={`report-row-${index}`}
                  >
                    <td className="px-3 py-4 align-top text-xs break-words">
                      <span
                        className="text-blue-600 font-semibold hover:text-blue-700 hover:underline cursor-pointer"
                        data-testid={`report-row-${index}-order-id`}
                      >
                        {order.order_number || order.id || 'N/A'}
                      </span>
                    </td>
                    <td className="px-3 py-4 align-top text-xs text-slate-700 break-words" data-testid={`report-row-${index}-product`}>
                      {/* Jun 2026 — Orders-Grid parity: combined display
                          ("Recon - FM001-RE") when the backend provides it. */}
                      {order.product_display || order.client_product || order.product_type || 'N/A'}
                    </td>
                    <td className="px-3 py-4 align-top text-xs text-slate-700 break-words">
                      {normalizeBorrowerName(order.borrower_name || order.customer_name) || 'N/A'}
                      {order.seller_name && (
                        <div className="text-[11px] text-slate-500" data-testid={`report-seller-${order.id}`}>Seller: {normalizeBorrowerName(order.seller_name) || order.seller_name}</div>
                      )}
                    </td>
                    <td className="px-3 py-4 align-top text-xs text-slate-700 break-words" title={formatPropertyAddress(order)}>
                      {formatPropertyAddress(order)}
                    </td>
                    <td className="px-3 py-4 align-top text-xs">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize whitespace-nowrap ${statusChipClasses(order.status)}`}>
                        {order.status || 'unknown'}
                      </span>
                    </td>
                    <td className="px-3 py-4 align-top text-xs">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize whitespace-nowrap ${priorityChipClasses(order.priority)}`}>
                        {order.priority || 'normal'}
                      </span>
                    </td>
                    <td className="px-3 py-4 align-top text-xs text-slate-700 break-words">
                      {order.client_code || 'N/A'}
                    </td>
                    <td className="px-3 py-4 align-top text-xs text-slate-700 break-words">
                      {order.assignee_name || order.assigned_team || 'Unassigned'}
                    </td>
                    <td
                      className="px-3 py-4 align-top text-xs cursor-help break-words"
                      title={order.created_at && getTimestampTooltip ? getTimestampTooltip(order.created_at) : ''}
                    >
                      <div className="text-slate-900 font-medium leading-tight">{created.datePart}</div>
                      {created.timePart && <div className="text-slate-500 text-[11px] mt-0.5 leading-tight">{created.timePart}</div>}
                    </td>
                    <td
                      className="px-3 py-4 align-top text-xs cursor-help break-words"
                      title={order.updated_at && getTimestampTooltip ? getTimestampTooltip(order.updated_at) : ''}
                    >
                      <div className="text-slate-900 font-medium leading-tight">{updated.datePart}</div>
                      {updated.timePart && <div className="text-slate-500 text-[11px] mt-0.5 leading-tight">{updated.timePart}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalOrders > pageSize && (
          <ReportPagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalOrders={totalOrders}
            pageSize={pageSize}
          />
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Report Pagination Component
 */
export const ReportPagination = ({
  currentPage,
  setCurrentPage,
  totalOrders,
  pageSize = 50
}) => {
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, totalOrders);
  const hasNextPage = currentPage * pageSize < totalOrders;
  const hasPrevPage = currentPage > 1;

  return (
    <div className="flex items-center justify-between p-4 border-t border-slate-200">
      <div className="text-sm text-slate-600">
        Showing {startIndex} to {endIndex} of {totalOrders} orders
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={!hasPrevPage}
          onClick={() => setCurrentPage(currentPage - 1)}
          data-testid="report-pagination-prev"
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={!hasNextPage}
          onClick={() => setCurrentPage(currentPage + 1)}
          data-testid="report-pagination-next"
        >
          Next
        </Button>
      </div>
    </div>
  );
};

/**
 * Report Loading State
 */
export const ReportLoadingState = () => (
  <Card className="shadow-sm">
    <CardContent className="p-8">
      <div className="flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
        <span className="text-gray-600">Running report...</span>
      </div>
    </CardContent>
  </Card>
);

/**
 * Report Error State
 */
export const ReportErrorState = ({ error, onRetry }) => (
  <Card className="border-rose-200 bg-rose-50 shadow-sm">
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-rose-900">Report Error</p>
          <p className="text-sm text-rose-700">{error}</p>
          <p className="text-xs text-rose-600 mt-1">Try narrowing your filters if the data set is large.</p>
        </div>
        {onRetry && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            className="border-rose-300 text-rose-700 hover:bg-rose-100"
          >
            Retry
          </Button>
        )}
      </div>
    </CardContent>
  </Card>
);
